import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Add,
  Refresh,
  MoreVert,
  Edit,
  SwapHoriz,
  Business,
  CalendarToday,
  DragIndicator,
  Person,
  FilterList,
} from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  TableEmptyState,
  TypePill,
  StatusDot,
  StatusTone,
  FilterBar,
  FilterSpacer,
} from '../../components/common/pageKit';
import { formatCurrency, formatIndianNumber, formatIndianDate } from '../../utils/currency';
import { api } from '../../services/apiClient';
import crmService, { FUNNEL_ORDER, FunnelStage, PipelineSummary } from '../../services/crmService';

// ---------------------------------------------------------------------------
// Sales opportunity — the actual deal record. Pipeline cards and the
// create/edit dialog talk to the EXISTING /sales/opportunities CRUD
// (per the task brief) rather than duplicating it in the CRM module; only
// the stage move goes through the CRM endpoint, because that's the one that
// also logs a crm_activity for the move.
// ---------------------------------------------------------------------------

interface SalesOpportunity {
  id: number;
  leadId: number | null;
  customerId: number | null;
  customerName: string | null;
  customerContact: string | null;
  opportunityName: string;
  estimatedValue: number;
  estimatedValueFormatted: string;
  probability: number;
  expectedClosureDate: string | null;
  stage: FunnelStage;
  assignedTo: number | null;
  assignedToName: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface SalesCustomer {
  id: number;
  company_name: string;
  contact_person: string | null;
}

interface OpportunityFormData {
  opportunityName: string;
  customerId: number | null;
  estimatedValue: number;
  probability: number;
  expectedClosureDate: string;
  stage: FunnelStage;
  notes: string;
}

const emptyForm: OpportunityFormData = {
  opportunityName: '',
  customerId: null,
  estimatedValue: 0,
  probability: 0,
  expectedClosureDate: '',
  stage: 'PROSPECTING',
  notes: '',
};

/** Stage identity colour — deliberately its own palette, distinct from any
 * status colouring used elsewhere (StatusDot on LeadsPage, etc.) so a viewer
 * never conflates "which column" with "good/bad". */
const stageTints: Record<FunnelStage, string> = {
  PROSPECTING: '#64748b',
  QUALIFICATION: '#0284c7',
  PROPOSAL: '#7c3aed',
  NEGOTIATION: '#ea580c',
  WON: '#059669',
  LOST: '#dc2626',
};

const stageLabels: Record<FunnelStage, string> = {
  PROSPECTING: 'Prospecting',
  QUALIFICATION: 'Qualification',
  PROPOSAL: 'Proposal',
  NEGOTIATION: 'Negotiation',
  WON: 'Won',
  LOST: 'Lost',
};

const weightedValue = (opp: SalesOpportunity) => (opp.estimatedValue * opp.probability) / 100;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Whole days since `iso` — used both for "stage age" (from updated_at, since a
 * stage move is what bumps that column) and for close-date overdue checks. */
const daysSince = (iso: string): number => Math.floor((Date.now() - new Date(iso).getTime()) / MS_PER_DAY);

/** Client-side deal "temperature" from data the /sales/opportunities list
 * already carries — no invented scores. Overdue beats stale: a deal whose
 * expected close date has passed is worse than one that's merely dwelt long
 * in its stage. Closed deals (WON/LOST) are never flagged. */
const dealTemperature = (opp: SalesOpportunity): { label: string; tone: StatusTone } => {
  if (opp.stage === 'WON' || opp.stage === 'LOST') return { label: 'Closed', tone: 'neutral' };
  if (opp.expectedClosureDate && new Date(opp.expectedClosureDate).getTime() < Date.now()) {
    return { label: 'Overdue', tone: 'error' };
  }
  if (daysSince(opp.updatedAt) > 21) return { label: 'Stale', tone: 'warning' };
  return { label: 'On track', tone: 'success' };
};

const PipelinePage: React.FC = () => {
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState<SalesOpportunity[]>([]);
  const [customers, setCustomers] = useState<SalesCustomer[]>([]);
  const [summary, setSummary] = useState<PipelineSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // In-flight stage moves, so a card can show a quiet "saving" affordance
  // instead of looking idle while its optimistic move is unconfirmed.
  const [savingIds, setSavingIds] = useState<Set<number>>(new Set());
  const [dragOverStage, setDragOverStage] = useState<FunnelStage | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);

  const [cardMenu, setCardMenu] = useState<{ anchor: HTMLElement; opp: SalesOpportunity } | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOpp, setEditingOpp] = useState<SalesOpportunity | null>(null);
  const [form, setForm] = useState<OpportunityFormData>(emptyForm);
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  // Board filters — applied client-side to the already-loaded list, so
  // filtering never touches drag-drop (moveOpportunity always resolves the
  // dragged id against the full `opportunities` state, filtered or not).
  const [ownerFilter, setOwnerFilter] = useState('');
  const [minValueFilter, setMinValueFilter] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [oppRes, summaryRes] = await Promise.all([
        api.get<{ opportunities: SalesOpportunity[] }>('/sales/opportunities'),
        crmService.getPipelineSummary(),
      ]);
      setOpportunities(oppRes.opportunities || []);
      setSummary(summaryRes);
    } catch (err: any) {
      setError(err.message || 'Failed to load the pipeline');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await api.get<{ customers: SalesCustomer[] }>('/sales/customers');
      setCustomers(res.customers || []);
    } catch {
      // Non-fatal — the dialog's customer picker just stays empty.
    }
  }, []);

  useEffect(() => {
    fetchAll();
    fetchCustomers();
  }, [fetchAll, fetchCustomers]);

  const owners = useMemo(
    () =>
      Array.from(new Set(opportunities.map((o) => o.assignedToName).filter((n): n is string => !!n))).sort(),
    [opportunities]
  );

  const filteredOpportunities = useMemo(() => {
    const minValue = minValueFilter ? parseFloat(minValueFilter) : null;
    return opportunities.filter((o) => {
      if (ownerFilter && o.assignedToName !== ownerFilter) return false;
      if (minValue !== null && !Number.isNaN(minValue) && o.estimatedValue < minValue) return false;
      return true;
    });
  }, [opportunities, ownerFilter, minValueFilter]);

  const columns = useMemo(() => {
    return FUNNEL_ORDER.map((stage) => {
      const deals = filteredOpportunities.filter((o) => o.stage === stage);
      const totalValue = deals.reduce((sum, o) => sum + o.estimatedValue, 0);
      const totalWeighted = deals.reduce((sum, o) => sum + weightedValue(o), 0);
      return { stage, deals, totalValue, totalWeighted };
    });
  }, [filteredOpportunities]);

  // ---------------------------------------------------------------------
  // Stage move — optimistic with rollback. Shared by drag-drop AND the
  // card menu's "Move to" fallback, so both paths behave identically.
  // ---------------------------------------------------------------------
  const moveOpportunity = useCallback(
    async (id: number, newStage: FunnelStage) => {
      const previous = opportunities;
      const target = previous.find((o) => o.id === id);
      if (!target || target.stage === newStage) return;

      const optimisticProbability =
        newStage === 'WON' ? 100 : newStage === 'LOST' ? 0 : target.probability;

      setOpportunities((prev) =>
        prev.map((o) =>
          o.id === id ? { ...o, stage: newStage, probability: optimisticProbability } : o
        )
      );
      setSavingIds((prev) => new Set(prev).add(id));

      try {
        await crmService.updateOpportunityStage(id, { stage: newStage });
      } catch (err: any) {
        // Roll back — a card that silently fails to save is worse than one
        // that visibly snaps back with an explanation.
        setOpportunities(previous);
        setToast({
          message: err.message || `Could not move "${target.opportunityName}" — reverted.`,
          severity: 'error',
        });
      } finally {
        setSavingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [opportunities]
  );

  // ---- Drag and drop (HTML5 native) ----
  const handleDragStart = (e: React.DragEvent, id: number) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(id));
  };
  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverStage(null);
  };
  const handleColumnDragOver = (e: React.DragEvent, stage: FunnelStage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverStage !== stage) setDragOverStage(stage);
  };
  const handleColumnDrop = (e: React.DragEvent, stage: FunnelStage) => {
    e.preventDefault();
    const idStr = e.dataTransfer.getData('text/plain');
    const id = idStr ? parseInt(idStr, 10) : draggingId;
    setDragOverStage(null);
    setDraggingId(null);
    if (id !== null && !Number.isNaN(id)) {
      moveOpportunity(id, stage);
    }
  };

  // ---- Create / edit dialog (reuses /sales/opportunities CRUD) ----
  const openCreateDialog = (stage?: FunnelStage) => {
    setEditingOpp(null);
    setForm({ ...emptyForm, stage: stage || 'PROSPECTING' });
    setDialogOpen(true);
  };
  const openEditDialog = (opp: SalesOpportunity) => {
    setEditingOpp(opp);
    setForm({
      opportunityName: opp.opportunityName,
      customerId: opp.customerId,
      estimatedValue: opp.estimatedValue,
      probability: opp.probability,
      expectedClosureDate: opp.expectedClosureDate ? opp.expectedClosureDate.split('T')[0] : '',
      stage: opp.stage,
      notes: opp.notes || '',
    });
    setDialogOpen(true);
    setCardMenu(null);
  };

  const handleSaveOpportunity = async () => {
    setSaving(true);
    try {
      if (editingOpp) {
        await api.put(`/sales/opportunities/${editingOpp.id}`, form);
        setToast({ message: 'Opportunity updated', severity: 'success' });
      } else {
        await api.post('/sales/opportunities', form);
        setToast({ message: 'Opportunity created', severity: 'success' });
      }
      setDialogOpen(false);
      await fetchAll();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to save the opportunity', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const fmtMoney = (n: number | null) => (n === null ? '—' : formatCurrency(n));
  const fmtPct = (n: number | null) => (n === null ? '—' : `${n}%`);
  const fmtDays = (n: number | null) => (n === null ? '—' : `${Math.round(n)} d`);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <PageHeader
        title="Pipeline"
        subtitle="Every open opportunity, grouped by stage — drag a card to move it, or use its menu"
        actions={
          <>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchAll} disabled={loading}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<Add />} size="small" onClick={() => openCreateDialog()}>
              New Opportunity
            </Button>
          </>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {summary && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, 1fr)' },
            gap: 2,
            mb: 2.5,
          }}
        >
          <StatCard title="Open Deals" value={summary.totalOpen} icon={<SwapHoriz />} color="#0284c7" />
          <StatCard
            title="Weighted Forecast"
            value={fmtMoney(summary.weightedForecast)}
            icon={<DragIndicator />}
            color="#7c3aed"
          />
          <StatCard title="Win Rate" value={fmtPct(summary.winRatePct)} icon={<Business />} color="#059669" />
          <StatCard
            title="Avg Deal Size"
            value={fmtMoney(summary.avgDealSize)}
            icon={<CalendarToday />}
            color="#ea580c"
          />
          <StatCard
            title="Avg Sales Cycle"
            value={fmtDays(summary.avgSalesCycleDays)}
            icon={<Refresh />}
            color="#64748b"
          />
        </Box>
      )}

      {!loading && opportunities.length > 0 && (
        <FilterBar>
          <TextField
            select
            size="small"
            label="Owner"
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)}
            sx={{ minWidth: 180 }}
            InputProps={{
              startAdornment: <FilterList sx={{ fontSize: 18, color: 'text.disabled', mr: 0.5 }} />,
            }}
          >
            <MenuItem value="">All Owners</MenuItem>
            {owners.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Min Value"
            type="number"
            value={minValueFilter}
            onChange={(e) => setMinValueFilter(e.target.value)}
            sx={{ minWidth: 150 }}
          />
          <FilterSpacer />
          {(ownerFilter || minValueFilter) && (
            <Button
              size="small"
              onClick={() => {
                setOwnerFilter('');
                setMinValueFilter('');
              }}
            >
              Clear Filters
            </Button>
          )}
        </FilterBar>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        // Board scrolls horizontally within ITSELF when six columns don't
        // fit — the page/body never gains horizontal scroll because this
        // box, not the page, is the overflow container.
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            overflowY: 'hidden',
            pb: 1,
            flexGrow: 1,
            minHeight: 0,
          }}
        >
          {columns.map(({ stage, deals, totalValue, totalWeighted }) => {
            const tint = stageTints[stage];
            const isDragOver = dragOverStage === stage;
            return (
              <Box
                key={stage}
                role="region"
                aria-label={`${stageLabels[stage]} — ${deals.length} deals`}
                onDragOver={(e) => handleColumnDragOver(e, stage)}
                onDragLeave={() => setDragOverStage((s) => (s === stage ? null : s))}
                onDrop={(e) => handleColumnDrop(e, stage)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: 296,
                  flexShrink: 0,
                  bgcolor: isDragOver ? `${tint}0f` : 'transparent',
                  borderRadius: 3,
                  transition: 'background-color .15s ease',
                }}
              >
                {/* Column header */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    mb: 1.25,
                    borderRadius: 2.5,
                    border: '1px solid',
                    borderColor: isDragOver ? tint : 'divider',
                    borderTop: '3px solid',
                    borderTopColor: tint,
                    transition: 'border-color .15s ease',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <TypePill label={stageLabels[stage]} tint={tint} />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {deals.length}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{ fontWeight: 700, fontSize: '0.9rem', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatIndianNumber(totalValue)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    Weighted {formatIndianNumber(totalWeighted)}
                  </Typography>
                </Paper>

                {/* Cards */}
                <Box
                  sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    minHeight: 120,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    pb: 1,
                    pr: 0.5,
                  }}
                >
                  {deals.length === 0 ? (
                    <Box
                      sx={{
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 2,
                        py: 3,
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="caption" color="text.disabled">
                        No deals in this stage
                      </Typography>
                    </Box>
                  ) : (
                    deals.map((opp) => {
                      const isSaving = savingIds.has(opp.id);
                      const isDragging = draggingId === opp.id;
                      return (
                        <Paper
                          key={opp.id}
                          elevation={0}
                          draggable
                          onDragStart={(e) => handleDragStart(e, opp.id)}
                          onDragEnd={handleDragEnd}
                          tabIndex={0}
                          role="article"
                          aria-roledescription="Draggable opportunity card"
                          aria-label={`${opp.opportunityName}, ${formatCurrency(opp.estimatedValue)}, stage ${stageLabels[stage]}`}
                          onClick={() => navigate(`/crm/opportunity/${opp.id}/workspace`)}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            cursor: 'grab',
                            opacity: isDragging ? 0.5 : isSaving ? 0.7 : 1,
                            transition: 'opacity .15s ease, box-shadow .15s ease, transform .15s ease',
                            '&:hover': { boxShadow: 2 },
                            '&:focus-visible': { outline: `2px solid ${tint}`, outlineOffset: 1 },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem', lineHeight: 1.3 }}>
                              {opp.opportunityName}
                            </Typography>
                            <IconButton
                              size="small"
                              aria-label={`Change stage or edit ${opp.opportunityName}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCardMenu({ anchor: e.currentTarget, opp });
                              }}
                              sx={{ mt: -0.5, mr: -0.5, flexShrink: 0 }}
                            >
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </Box>

                          {opp.customerName && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                              <Business sx={{ fontSize: 13, color: 'text.disabled' }} />
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {opp.customerName}
                              </Typography>
                            </Box>
                          )}

                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, mt: 0.5 }}>
                            {opp.assignedToName ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                                <Person sx={{ fontSize: 13, color: 'text.disabled' }} />
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {opp.assignedToName}
                                </Typography>
                              </Box>
                            ) : (
                              <span />
                            )}
                            <StatusDot label={dealTemperature(opp).label} tone={dealTemperature(opp).tone} />
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mt: 1 }}>
                            <Typography
                              title={formatCurrency(opp.estimatedValue)}
                              sx={{ fontWeight: 700, fontSize: '0.875rem', fontVariantNumeric: 'tabular-nums' }}
                            >
                              {formatIndianNumber(opp.estimatedValue)}
                            </Typography>
                            <Chip
                              label={`${opp.probability}%`}
                              size="small"
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.6875rem', fontVariantNumeric: 'tabular-nums' }}
                            />
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}
                          >
                            Weighted {formatIndianNumber(weightedValue(opp))}
                          </Typography>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                            <CalendarToday sx={{ fontSize: 12, color: 'text.disabled' }} />
                            <Typography variant="caption" color="text.secondary">
                              {opp.expectedClosureDate
                                ? formatIndianDate(new Date(opp.expectedClosureDate))
                                : '—'}
                            </Typography>
                            <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
                              {isSaving ? <CircularProgress size={10} /> : `${daysSince(opp.updatedAt)}d in stage`}
                            </Typography>
                          </Box>
                        </Paper>
                      );
                    })
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {!loading && opportunities.length === 0 && (
        <TableEmptyState title="No opportunities yet" hint="Create one, or convert a qualified lead." />
      )}

      {/* Card menu — the non-drag, keyboard-reachable way to move a card. */}
      <Menu
        anchorEl={cardMenu?.anchor || null}
        open={!!cardMenu}
        onClose={() => setCardMenu(null)}
      >
        <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary' }}>
          Move to
        </Typography>
        {FUNNEL_ORDER.filter((s) => s !== cardMenu?.opp.stage).map((s) => (
          <MenuItem
            key={s}
            onClick={() => {
              if (cardMenu) moveOpportunity(cardMenu.opp.id, s);
              setCardMenu(null);
            }}
          >
            <ListItemIcon>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: stageTints[s] }} />
            </ListItemIcon>
            <ListItemText>{stageLabels[s]}</ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={() => cardMenu && openEditDialog(cardMenu.opp)}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit opportunity</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingOpp ? 'Edit Opportunity' : 'New Opportunity'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Opportunity Name"
              value={form.opportunityName}
              onChange={(e) => setForm((f) => ({ ...f, opportunityName: e.target.value }))}
              required
            />
            <Autocomplete
              options={customers}
              getOptionLabel={(o) => o.company_name || ''}
              value={customers.find((c) => c.id === form.customerId) || null}
              onChange={(_, v) => setForm((f) => ({ ...f, customerId: v?.id ?? null }))}
              renderInput={(params) => <TextField {...params} label="Customer" />}
            />
            <TextField
              select
              fullWidth
              label="Stage"
              value={form.stage}
              onChange={(e) => setForm((f) => ({ ...f, stage: e.target.value as FunnelStage }))}
            >
              {FUNNEL_ORDER.map((s) => (
                <MenuItem key={s} value={s}>
                  {stageLabels[s]}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Estimated Value"
                type="number"
                value={form.estimatedValue}
                onChange={(e) => setForm((f) => ({ ...f, estimatedValue: parseFloat(e.target.value) || 0 }))}
              />
              <TextField
                fullWidth
                label="Probability (%)"
                type="number"
                inputProps={{ min: 0, max: 100 }}
                value={form.probability}
                onChange={(e) =>
                  setForm((f) => ({ ...f, probability: Math.min(100, Math.max(0, parseInt(e.target.value, 10) || 0)) }))
                }
              />
            </Box>
            <TextField
              fullWidth
              label="Expected Close Date"
              type="date"
              value={form.expectedClosureDate}
              onChange={(e) => setForm((f) => ({ ...f, expectedClosureDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Notes"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveOpportunity}
            disabled={saving || !form.opportunityName.trim()}
          >
            {saving ? 'Saving…' : editingOpp ? 'Save Changes' : 'Create Opportunity'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default PipelinePage;
